'use client';

import { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import { useGSAP } from '@gsap/react';
import Icon from '@/components/ui/AppIcon';
import MobileProcessTimeline from './MobileProcessTimeline';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);
}

const ProcessTimeline = () => {
    const containerRef = useRef<HTMLElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);

    useGSAP(() => {
        const svg = svgRef.current;
        const path = containerRef.current?.querySelector('#process-path') as SVGPathElement;

        if (!svg || !path) return;

        // Get path length for draw animation
        const length = path.getTotalLength();

        // Set up the "Snake" effect (traveling dashed line)
        // The dash will be the "active" segment, the rest is gap.
        const pathLength = path.getTotalLength();
        const dashLength = pathLength * 0.25; // Length of the "shiny" part (adjusted as needed)

        // Prepare the stroke parameters
        gsap.set(path, {
            strokeDasharray: `${dashLength} ${pathLength}`, // dash, gap
            strokeDashoffset: dashLength, // Start with dash potentially just hidden or at very top
        });

        // Animate the dash traveling down the path
        gsap.to(path, {
            strokeDashoffset: -pathLength, // Move it all the way to the end
            ease: "none",
            scrollTrigger: {
                trigger: containerRef.current,
                start: "top center",
                end: "bottom center",
                scrub: 1,
            }
        });

        // Reveal steps as we scroll
        const steps = gsap.utils.toArray<HTMLElement>('.process-step');
        steps.forEach((step, i) => {
            gsap.from(step, {
                opacity: 0,
                y: 50,
                duration: 0.8,
                scrollTrigger: {
                    trigger: step,
                    start: "top 80%",
                    toggleActions: "play none none reverse",
                }
            });
        });

    }, { scope: containerRef });

    const steps = [
        {
            id: 1,
            title: "Discovery",
            description: "We dive deep into your business goals, architectural needs, and market position.",
            icon: "MagnifyingGlassIcon",
            align: "left"
        },
        {
            id: 2,
            title: "Strategy",
            description: "Developing a roadmap that combines technical scalability with SEO dominance.",
            icon: "MapIcon",
            align: "right"
        },
        {
            id: 3,
            title: "Development",
            description: "Agile build cycles with constant feedback, ensuring pixel-perfect implementation.",
            icon: "CodeBracketSquareIcon",
            align: "left"
        },
        {
            id: 4,
            title: "Launch & Scale",
            description: "Deployment to edge networks followed by aggressive visibility campaigns.",
            icon: "RocketLaunchIcon",
            align: "right"
        }
    ];

    return (
        <>
            {/* --- Desktop View (Existing) --- */}
            <div className="hidden lg:block">
                <section ref={containerRef} className="py-24 bg-surface relative overflow-hidden">

                    {/* Background Path SVG */}
                    <div className="absolute inset-0 w-full h-full pointer-events-none">
                        <svg ref={svgRef} className="w-full h-full" viewBox="-10 0 120 1200" preserveAspectRatio="none">
                            {/* Track Path (Faint) */}
                            <path
                                d="M50 0 Q50 200 20 300 T50 600 T80 1200"
                                fill="none"
                                stroke="rgba(0,0,0,0.05)"
                                strokeWidth="4"
                                vectorEffect="non-scaling-stroke"
                            />

                            {/* Animated Progress Path (Shiny Black) */}
                            <path
                                id="process-path"
                                d="M50 0 Q 50 200, 20 300 T 50 600 T 80 1200"
                                fill="none"
                                stroke="black"
                                strokeWidth="3"
                                vectorEffect="non-scaling-stroke"
                                className="drop-shadow-[0_0_2px_rgba(255,255,255,0.5)]"
                            />
                        </svg>
                    </div>

                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="text-center mb-20">
                            <h2 className="text-3xl sm:text-5xl font-bold text-text-primary mb-6">The Journey</h2>
                            <p className="text-text-secondary text-lg">From concept to dominance in four steps.</p>
                        </div>

                        <div className="space-y-24 relative">
                            {steps.map((step, index) => (
                                <div
                                    key={step.id}
                                    className={`process-step flex items-center ${step.align === 'left' ? 'flex-row' : 'flex-row-reverse'} justify-between gap-8 md:gap-20`}
                                >
                                    {/* Content */}
                                    <div className={`w-1/2 ${step.align === 'left' ? 'text-right' : 'text-left'}`}>
                                        <h3 className="text-2xl font-bold text-text-primary mb-4">{step.title}</h3>
                                        <p className="text-text-secondary leading-relaxed">{step.description}</p>
                                    </div>

                                    {/* Center Marker */}
                                    <div className="relative flex-shrink-0">
                                        <div className="w-16 h-16 rounded-full bg-card border border-border flex items-center justify-center shadow-xl z-20 relative">
                                            <Icon name={step.icon as any} size={28} className="text-brand-primary" />
                                        </div>
                                    </div>

                                    {/* Empty space for balance */}
                                    <div className="w-1/2"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </div>

            {/* --- Mobile View (New Vertical Snake) --- */}
            <div className="block lg:hidden">
                <MobileProcessTimeline />
            </div>
        </>
    );
};

export default ProcessTimeline;
